import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import type { NewRequisitionType } from "../NewRequisition/types";
import {
  formatDateTimeFromEpoch,
  formatWithTimezoneOffset,
} from "../../utils/helpers";
import {
  CfcCheckbox,
  CfcCloseButton,
  CfcOrderDetailsBody,
  CfcOrderDetailsCheckboxRow,
  CfcOrderDetailsCondition,
  CfcOrderDetailsConditionList,
  CfcOrderDetailsEmptyValue,
  CfcOrderDetailsHeader,
  CfcOrderDetailsIcdCode,
  CfcOrderDetailsIcdGrid,
  CfcOrderDetailsIcdSection,
  CfcOrderDetailsIntro,
  CfcOrderDetailsLabel,
  CfcOrderDetailsLead,
  CfcOrderDetailsMeta,
  CfcOrderDetailsOtherIcd,
  CfcOrderDetailsOtherIcdLabel,
  CfcOrderDetailsOtherIcdValue,
  CfcOrderDetailsPatientGrid,
  CfcOrderDetailsPhlebotomy,
  CfcOrderDetailsSection,
  CfcOrderDetailsStyledDialog,
  CfcOrderDetailsSubheader,
  CfcOrderDetailsTitle,
  CfcOrderDetailsValue,
  CfcPhlebotomyCopy,
  CfcPhlebotomyDescription,
  CfcPhlebotomyTitle,
} from "./CfcOrderDetailsDialog.styles";
import { CFC_CONDITIONS } from "./cfcConditions";

interface CfcOrderDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  orderData: CfcOrderDetailsData;
}

const EMPTY_FIELD_VALUE = "-";
const CFC_ORDER_DETAILS_TEXT = {
  title: "Order Details",
  closeAriaLabel: "Close order details",
  orderNumber: "Order Number:",
  createdAt: "Created At:",
  createdBy: "Created By:",
  intro:
    "Information for this patient has been pre-filled from the prior Unity Aneuploidy Screen (cfDNA) order.",
  selectedConditionsLead:
    "Selected condition(s) submitted with this Unity Confirm order:",
  icdSectionTitle: "ICD-10 Codes",
  icdSectionLead: "The ICD-10 selections below were submitted with this order.",
  abnormalIcdAriaLabel: "Abnormal chromosomal and genetic findings",
  abnormalIcdLabel: "Abnormal chromosomal & genetic findings",
  abnormalIcdCode: "O28.5",
  otherIcdCodes: "Other ICD-10 codes",
  phlebotomyAriaLabel: "Phlebotomy requested",
  phlebotomyTitle: "Request phlebotomy",
  phlebotomyDescription:
    "If this patient requires phlebotomy coordination, please check this box and a BillionToOne Client Services representative will follow up with the patient to schedule their blood draw.",
} as const;

const PATIENT_DETAIL_LABELS = {
  firstName: "First name",
  middleInitial: "Middle initial",
  lastName: "Last name",
  sex: "Sex",
  dateOfBirth: "Date of birth",
  ethnicity: "Ethnicity",
  gestation: "Gestation",
  maternalWeight: "Maternal Weight (lbs)",
  estimatedDueDate: "Estimated due date",
} as const;

const GESTATION_LABELS = {
  notPregnant: "Not pregnant",
  twin: "Twin pregnancy",
  tripletOrMore: "Triplet or more pregnancy",
  singleton: "Singleton pregnancy",
} as const;

type CfcOrderDetailsField =
  | "unity_complete_id"
  | "created_at"
  | "provider_full_name"
  | "first_name"
  | "middle_initial"
  | "last_name"
  | "sex"
  | "date_of_birth"
  | "is_asian"
  | "is_african"
  | "is_ashkenazi"
  | "is_middle_eastern"
  | "is_french_canadian"
  | "is_hispanic"
  | "is_other_ethnicity"
  | "is_unknown_ethnicity"
  | "is_white"
  | "is_northern_european"
  | "is_pregnant"
  | "is_twin"
  | "is_triplet_or_more"
  | "maternal_weight"
  | "estimated_due_date"
  | "selected_conditions"
  | "is_O28_5_aneuploidy"
  | "other_aneuploidy_icd_10_codes"
  | "is_mobile_phlebotomy";

export type CfcOrderDetailsData = Pick<
  NewRequisitionType,
  CfcOrderDetailsField
> & { is_cfc_order: true };

const WHITE_ETHNICITY_LABEL = "White";

const ETHNICITY_FIELDS: Array<[keyof CfcOrderDetailsData, string]> = [
  ["is_asian", "Asian"],
  ["is_african", "African/African American"],
  ["is_ashkenazi", "Ashkenazi Jewish"],
  ["is_middle_eastern", "Middle Eastern"],
  ["is_french_canadian", "French Canadian or Cajun"],
  ["is_hispanic", "Hispanic"],
  ["is_other_ethnicity", "Other"],
  ["is_unknown_ethnicity", "Unknown"],
];

export const isCfcOrder = <T extends { is_cfc_order?: boolean }>(
  orderData: T | null | undefined,
): orderData is T & { is_cfc_order: true } => orderData?.is_cfc_order === true;

const getDisplayValue = (value?: string | number | null): string => {
  if (value === undefined || value === null) return EMPTY_FIELD_VALUE;
  const stringValue = String(value).trim();
  return stringValue || EMPTY_FIELD_VALUE;
};

const getDisplayDate = (value?: string | null): string =>
  formatWithTimezoneOffset(value || undefined) || EMPTY_FIELD_VALUE;

const getDisplayDateTime = (value?: number | string): string =>
  formatDateTimeFromEpoch(value) || EMPTY_FIELD_VALUE;

const getEthnicity = (orderData: CfcOrderDetailsData): string => {
  const ethnicity = ETHNICITY_FIELDS.flatMap(([field, label]) =>
    orderData[field] ? [label] : [],
  );

  if (orderData.is_white || orderData.is_northern_european) {
    ethnicity.push(WHITE_ETHNICITY_LABEL);
  }

  return ethnicity.join(", ") || EMPTY_FIELD_VALUE;
};

const getGestation = (orderData: CfcOrderDetailsData): string => {
  if (orderData.is_pregnant == null) return EMPTY_FIELD_VALUE;
  if (!orderData.is_pregnant) return GESTATION_LABELS.notPregnant;
  if (orderData.is_twin) return GESTATION_LABELS.twin;
  if (orderData.is_triplet_or_more) return GESTATION_LABELS.tripletOrMore;
  return GESTATION_LABELS.singleton;
};

const getMaternalWeight = (value?: string): string => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return EMPTY_FIELD_VALUE;
  const parsed = Number(trimmedValue);
  if (Number.isNaN(parsed)) return trimmedValue;
  if (parsed === 0) return EMPTY_FIELD_VALUE;
  return String(parsed);
};

const getSelectedConditionLabel = (selectedCondition: string): string =>
  CFC_CONDITIONS.find((condition) => condition.code === selectedCondition)
    ?.label ?? selectedCondition;

const getSelectedConditionLabels = (selectedConditions?: string[]): string[] =>
  (selectedConditions || []).map(getSelectedConditionLabel);

const getPatientDetailRows = (orderData: CfcOrderDetailsData) => [
  {
    label: PATIENT_DETAIL_LABELS.firstName,
    value: getDisplayValue(orderData.first_name),
  },
  {
    label: PATIENT_DETAIL_LABELS.middleInitial,
    value: getDisplayValue(orderData.middle_initial),
  },
  {
    label: PATIENT_DETAIL_LABELS.lastName,
    value: getDisplayValue(orderData.last_name),
  },
  { label: PATIENT_DETAIL_LABELS.sex, value: getDisplayValue(orderData.sex) },
  {
    label: PATIENT_DETAIL_LABELS.dateOfBirth,
    value: getDisplayDate(orderData.date_of_birth),
  },
  { label: PATIENT_DETAIL_LABELS.ethnicity, value: getEthnicity(orderData) },
  { label: PATIENT_DETAIL_LABELS.gestation, value: getGestation(orderData) },
  {
    label: PATIENT_DETAIL_LABELS.maternalWeight,
    value: getMaternalWeight(orderData.maternal_weight),
  },
  {
    label: PATIENT_DETAIL_LABELS.estimatedDueDate,
    value: getDisplayDate(orderData.estimated_due_date),
  },
];

export const CfcOrderDetailsDialog: React.FC<CfcOrderDetailsDialogProps> = ({
  open,
  onClose,
  orderData,
}) => {
  const selectedConditionLabels = getSelectedConditionLabels(
    orderData.selected_conditions,
  );
  const otherIcdCodes = getDisplayValue(
    orderData.other_aneuploidy_icd_10_codes,
  );
  const isO285Selected = Boolean(orderData.is_O28_5_aneuploidy);
  const isPhlebotomyRequested = Boolean(orderData.is_mobile_phlebotomy);

  return (
    <CfcOrderDetailsStyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="cfc-order-details-title"
    >
      <CfcOrderDetailsHeader>
        <CfcOrderDetailsTitle id="cfc-order-details-title">
          {CFC_ORDER_DETAILS_TEXT.title}
        </CfcOrderDetailsTitle>
        <CfcCloseButton
          onClick={onClose}
          aria-label={CFC_ORDER_DETAILS_TEXT.closeAriaLabel}
        >
          <FontAwesomeIcon icon={faXmark} />
        </CfcCloseButton>
      </CfcOrderDetailsHeader>
      <CfcOrderDetailsBody>
        <CfcOrderDetailsMeta>
          <p>
            {CFC_ORDER_DETAILS_TEXT.orderNumber}{" "}
            <strong>{getDisplayValue(orderData.unity_complete_id)}</strong>
          </p>
          <p>
            {CFC_ORDER_DETAILS_TEXT.createdAt}{" "}
            <strong>{getDisplayDateTime(orderData.created_at)}</strong>
          </p>
          <p>
            {CFC_ORDER_DETAILS_TEXT.createdBy}{" "}
            <strong>{getDisplayValue(orderData.provider_full_name)}</strong>
          </p>
        </CfcOrderDetailsMeta>

        <CfcOrderDetailsIntro>
          {CFC_ORDER_DETAILS_TEXT.intro}
        </CfcOrderDetailsIntro>
        <CfcOrderDetailsPatientGrid>
          {getPatientDetailRows(orderData).map((row) => (
            <React.Fragment key={row.label}>
              <CfcOrderDetailsLabel>{row.label}</CfcOrderDetailsLabel>
              <CfcOrderDetailsValue>{row.value}</CfcOrderDetailsValue>
            </React.Fragment>
          ))}
        </CfcOrderDetailsPatientGrid>

        <CfcOrderDetailsSection>
          <CfcOrderDetailsLead>
            {CFC_ORDER_DETAILS_TEXT.selectedConditionsLead}
          </CfcOrderDetailsLead>
          {selectedConditionLabels.length ? (
            <CfcOrderDetailsConditionList>
              {selectedConditionLabels.map((condition) => (
                <CfcOrderDetailsCondition key={condition}>
                  {condition}
                </CfcOrderDetailsCondition>
              ))}
            </CfcOrderDetailsConditionList>
          ) : (
            <CfcOrderDetailsEmptyValue>
              {EMPTY_FIELD_VALUE}
            </CfcOrderDetailsEmptyValue>
          )}
        </CfcOrderDetailsSection>

        <CfcOrderDetailsIcdSection>
          <CfcOrderDetailsSubheader>
            {CFC_ORDER_DETAILS_TEXT.icdSectionTitle}
          </CfcOrderDetailsSubheader>
          <CfcOrderDetailsLead>
            {CFC_ORDER_DETAILS_TEXT.icdSectionLead}
          </CfcOrderDetailsLead>
          <CfcOrderDetailsIcdGrid>
            <CfcOrderDetailsCheckboxRow>
              <CfcCheckbox
                checked={isO285Selected}
                disabled
                inputProps={{
                  "aria-label": CFC_ORDER_DETAILS_TEXT.abnormalIcdAriaLabel,
                }}
              />
              <span>{CFC_ORDER_DETAILS_TEXT.abnormalIcdLabel}</span>
            </CfcOrderDetailsCheckboxRow>
            <CfcOrderDetailsIcdCode>
              {CFC_ORDER_DETAILS_TEXT.abnormalIcdCode}
            </CfcOrderDetailsIcdCode>
          </CfcOrderDetailsIcdGrid>
          <CfcOrderDetailsOtherIcd>
            <CfcOrderDetailsOtherIcdLabel>
              {CFC_ORDER_DETAILS_TEXT.otherIcdCodes}
            </CfcOrderDetailsOtherIcdLabel>
            <CfcOrderDetailsOtherIcdValue>
              {otherIcdCodes}
            </CfcOrderDetailsOtherIcdValue>
          </CfcOrderDetailsOtherIcd>
        </CfcOrderDetailsIcdSection>

        <CfcOrderDetailsPhlebotomy>
          <CfcCheckbox
            checked={isPhlebotomyRequested}
            disabled
            inputProps={{
              "aria-label": CFC_ORDER_DETAILS_TEXT.phlebotomyAriaLabel,
            }}
          />
          <CfcPhlebotomyCopy>
            <CfcPhlebotomyTitle>
              {CFC_ORDER_DETAILS_TEXT.phlebotomyTitle}
            </CfcPhlebotomyTitle>
            <CfcPhlebotomyDescription>
              {CFC_ORDER_DETAILS_TEXT.phlebotomyDescription}
            </CfcPhlebotomyDescription>
          </CfcPhlebotomyCopy>
        </CfcOrderDetailsPhlebotomy>
      </CfcOrderDetailsBody>
    </CfcOrderDetailsStyledDialog>
  );
};
